import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Button, 
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { green, red } from '@mui/material/colors';

const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  
  const paymentId = searchParams.get('payment_id');
  const orderId = searchParams.get('order_id');
  const status = searchParams.get('status');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    // Simulate loading delay for better UX
    const timer = setTimeout(() => {
      if (status === 'success') {
        const storedData = sessionStorage.getItem('paymentFormData');
        if (storedData) {
          try {
            setFormData(JSON.parse(storedData));
          } catch (e) {
            console.error('Error parsing stored data:', e);
          }
          // Clear the stored data
          sessionStorage.removeItem('paymentFormData');
        }
      }
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [status]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh' 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Processing your payment...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 4, 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      minHeight: '70vh'
    }}>
      <Paper elevation={3} sx={{ 
        p: 4, 
        maxWidth: 600, 
        width: '100%',
        textAlign: 'center'
      }}>
        {status === 'success' ? (
          <>
            <CheckCircleIcon sx={{ 
              fontSize: 80, 
              color: green[500],
              mb: 2
            }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Payment Successful!
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              Your payment has been processed successfully.
            </Alert>

            <Box sx={{ 
              textAlign: 'left', 
              mb: 3,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1
            }}>
              <Typography variant="body1">
                <strong>Transaction ID:</strong> {paymentId}
              </Typography>
              <Typography variant="body1">
                <strong>Order ID:</strong> {orderId}
              </Typography>
              {formData?.amount && (
                <Typography variant="body1">
                  <strong>Amount:</strong> ₹{formData.amount}
                </Typography>
              )}
              {formData?.proponentName && (
                <Typography variant="body1">
                  <strong>Name:</strong> {formData.proponentName}
                </Typography>
              )}
            </Box>

            <Typography variant="body1" sx={{ mb: 3 }}>
              A receipt has been sent to your WhatsApp and email.
            </Typography>
          </>
        ) : (
          <>
            <ErrorIcon sx={{ 
              fontSize: 80, 
              color: red[500],
              mb: 2
            }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Payment Failed
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3 }}>
              There was an issue processing your payment. Please try again.
            </Alert>

            {paymentId && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                Reference ID: {paymentId}
              </Typography>
            )}
          </>
        )}

        <Button
          variant="contained"
          size="large"
          sx={{ mt: 3 }}
          onClick={() => navigate('/')}
        >
          Return to Home
        </Button>

        {status === 'success' && (
          <Button
            variant="outlined"
            size="large"
            sx={{ mt: 2, ml: 2 }}
            onClick={() => window.print()}
          >
            Print Receipt
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ThankYouPage;